import {
  useCallback,
  useEffect, useMemo, useState,
} from 'react';

import { DEFAULT_BBOX_STYLES } from '@vlabs/shared/components/photo-upload-form/BboxStyles';
import { imageTypes } from '@vlabs/shared/config/imageTypes';
import { Control } from '@vlabs/uikit';
import type { TFileDropzoneFile } from '@vlabs/uikit/components/controls/file-dropzone/FileDropzone';
import { useTranslation } from 'react-i18next';

import { FileWithDetectionsViewer } from '@vlabs/monokit/features/file-with-detections-viewer/FileWithDetectionsViewer';
import type { TBbox } from '@vlabs/monokit/shared/ui/canvas';

import st from './DetectionPicker.module.sass';
import { detectFaces } from '@vlabs/api/luna-api/client';
import type {
  DetectorFaceDetectionSample, DetectorSamples, Rect, SampleId,
} from '@vlabs/api/luna-api/model';

interface SubmitFunctionArgument {
  sampleId: SampleId;
}

interface ChangeFaceFormProps {
  onSubmit: (arg: SubmitFunctionArgument) => void;
  onCancel?: VoidFunction;
  imageFile?: TFileDropzoneFile | undefined;
}

function DetectionPicker({
  onSubmit,
  onCancel = undefined,
  imageFile = undefined,
}: ChangeFaceFormProps): JSX.Element {
  const { t } = useTranslation();
  const [file, setFile] = useState(imageFile);
  const [detections, setDetections] = useState<DetectorSamples[]>();
  const [selectedSampleId, setSelectedSampleId] = useState<SampleId>();

  const defaultFiles = useMemo(() => {
    const dt = new DataTransfer();
    if (imageFile?.raw) {
      dt.items.add(imageFile.raw);
    }
    return dt.files;
  }, [imageFile]);

  useEffect(() => {
    const requestDetections = async () => {
      if (!file?.raw) return;
      const detectedFaces = await detectFaces(file.raw, undefined, {
        headers: {
          'Content-type': file.raw.type,
        },
      });
      const samples = detectedFaces.images[0]?.detections?.samples;
      if ((samples ?? []).length > 0) {
        setDetections(samples);
      }
    };

    if (file) requestDetections();
  }, [file]);

  const boundingBoxes = useMemo(() => (detections?.map((detection) => ({
    ...((detection.face as DetectorFaceDetectionSample).detection.rect as Rect),
    id: (detection.face as DetectorFaceDetectionSample).sample_id as SampleId,
    isSelected: (detection.face as DetectorFaceDetectionSample).sample_id === selectedSampleId,
    style: DEFAULT_BBOX_STYLES,
    key: (detection.face as DetectorFaceDetectionSample).sample_id as SampleId,
  })) ?? []), [detections, selectedSampleId]);

  const onBBoxClick = useCallback((BBoxes: TBbox[] | null) => {
    if (!BBoxes) return;

    const bbox = BBoxes[0];
    const detectionId = bbox?.id;
    if (!detectionId) return;

    setSelectedSampleId(detectionId);
    const detection = detections?.find(($detection) => $detection?.face?.sample_id === detectionId);
    if (!detection?.face?.sample_id) return;
    setSelectedSampleId(detection.face.sample_id);
  }, [detections]);

  const handleReset = () => {
    setDetections(undefined);
    setSelectedSampleId(undefined);
    setFile(undefined);
  };

  const handleSubmit = () => {
    if (selectedSampleId && onSubmit) {
      onSubmit({
        sampleId: selectedSampleId,
      });
    }
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
  };

  const fileViewer = useMemo(() => {
    if (file?.raw?.type) {
      return ({
        [file.raw.type]: (
          <FileWithDetectionsViewer
            coloredBboxList={boundingBoxes}
            file={file}
            onBBoxClick={onBBoxClick}
          />
        ),
      });
    }

    return undefined;
  }, [file, boundingBoxes, onBBoxClick]);

  const validationError = (file && !detections?.length) ? t('features:changeFacePhoto.detection_picker.validation_error') : undefined;
  const validationDescription = (file && detections?.length) ? t('features:changeFacePhoto.detection_picker.validation_description', { count: detections?.length }) : undefined;

  return (
    <div className={st.DetectionPicker}>
      <h5>{t('features:changeFacePhoto.detection_picker.title')}</h5>
      <Control.FileDropzone
        defaultFile={defaultFiles}
        disabledWhenDetections
        error={detections?.length === 0}
        fileViewer={fileViewer}
        localFile={file?.raw}
        onChangeValue={setFile}
        onlyImages
        onReset={handleReset}
        supportedImageTypes={imageTypes}
        validationDescription={validationDescription}
        validationError={validationError}
      />
      <div className={st.Buttons}>
        <Control.Button
          disabled={selectedSampleId === undefined}
          onClick={handleSubmit}
        >
          {t('features:changeFacePhoto.detection_picker.submit_button')}
        </Control.Button>
        <Control.Button
          kind="secondary"
          onClick={handleCancel}
          variant="outlined"
        >
          {t('features:changeFacePhoto.detection_picker.cancel_button')}
        </Control.Button>
      </div>
    </div>
  );
}

export { DetectionPicker };
